import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Home, Save, Plus, Trash2, Download, LogOut, FileText, Users, Mail, Shield, Image, BarChart3, TrendingUp, Menu, X as CloseIcon, ClipboardList, Calendar, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Password change states
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Search and filter states for submissions
  const [submissionSearch, setSubmissionSearch] = useState('');
  const [submissionDateFilter, setSubmissionDateFilter] = useState('');
  const [submissionModelFilter, setSubmissionModelFilter] = useState('');

  useEffect(() => {
    verifyAuth();
  }, []);

  const verifyAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      fetchData();
    } catch (error) {
      toast.error('Session expirée. Veuillez vous reconnecter.');
      localStorage.removeItem('admin_token');
      navigate('/login');
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchData = async () => {
    try {
      const [settingsRes, submissionsRes] = await Promise.all([
        axios.get(`${API}/settings`, getAuthHeaders()),
        axios.get(`${API}/submissions?limit=200`, getAuthHeaders())
      ]);
      setSettings(settingsRes.data);
      // Gérer le nouveau format paginé
      const submissionsData = submissionsRes.data.submissions || submissionsRes.data;
      setSubmissions(Array.isArray(submissionsData) ? submissionsData : []);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, getAuthHeaders());
      toast.success('Paramètres sauvegardés avec succès');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const exportCSV = async () => {
    try {
      const response = await axios.get(`${API}/submissions/export`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'soumissions.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export CSV téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
      console.error(error);
    }
  };

  const downloadPrecontractPDF = async (submissionId, customerName) => {
    try {
      const response = await axios.get(`${API}/submissions/${submissionId}/pdf`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `precontrat_${customerName.replace(' ', '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Précontrat PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement PDF');
      console.error(error);
    }
  };

  const downloadPrecontractWord = async (submissionId, customerName) => {
    try {
      const response = await axios.get(`${API}/submissions/${submissionId}/docx`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `precontrat_${customerName.replace(' ', '_')}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Précontrat Word téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement Word');
      console.error(error);
    }
  };

  const deleteSubmission = async (submissionId, customerName) => {
    // Demander confirmation
    const confirmed = window.confirm(
      `Êtes-vous sûr de vouloir supprimer le précontrat de ${customerName} ?\n\n` +
      `⚠️ Cette action est irréversible.\n` +
      `Note : Si des contrats sont liés à ce précontrat, la suppression sera refusée.`
    );
    
    if (!confirmed) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/submissions/${submissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Précontrat de ${customerName} supprimé avec succès`);
      
      // Recharger la liste des soumissions
      fetchData();
    } catch (error) {
      console.error('Erreur suppression:', error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Impossible de supprimer : des contrats sont liés à ce précontrat');
      } else if (error.response?.status === 404) {
        toast.error('Précontrat non trouvé');
      } else {
        toast.error('Erreur lors de la suppression du précontrat');
      }
    }
  };

  // Filter submissions based on search and filters
  const filteredSubmissions = submissions.filter(sub => {
    const matchesSearch = submissionSearch === '' || 
      sub.customer.name.toLowerCase().includes(submissionSearch.toLowerCase()) ||
      sub.customer.email.toLowerCase().includes(submissionSearch.toLowerCase()) ||
      sub.project.model.toLowerCase().includes(submissionSearch.toLowerCase());
    
    const matchesDate = submissionDateFilter === '' ||
      new Date(sub.created_at).toLocaleDateString('fr-CA').includes(submissionDateFilter);
    
    const matchesModel = submissionModelFilter === '' ||
      sub.project.model.toLowerCase().includes(submissionModelFilter.toLowerCase());
    
    return matchesSearch && matchesDate && matchesModel;
  });

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Déconnexion réussie');
    navigate('/login');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Les nouveaux mots de passe ne correspondent pas');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Le nouveau mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        {
          old_password: oldPassword,
          new_password: newPassword
        },
        getAuthHeaders()
      );
      
      toast.success('Mot de passe changé avec succès');
      // Reset form
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Ancien mot de passe incorrect');
      } else {
        toast.error('Erreur lors du changement de mot de passe');
      }
      console.error(error);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleMigrateCRM = async () => {
    if (!window.confirm('Voulez-vous migrer toutes les soumissions existantes vers le CRM ? Cette opération ne doit être exécutée qu\'une seule fois.')) {
      return;
    }

    const loadingToast = toast.loading('Migration en cours...');
    try {
      const response = await axios.post(
        `${API}/crm/migrate-from-submissions`,
        {},
        getAuthHeaders()
      );

      toast.dismiss(loadingToast);
      const stats = response.data.statistics;
      
      if (stats.total_submissions === 0) {
        toast.info('Aucune soumission trouvée à migrer. Créez d\'abord des précontrats depuis le formulaire public.', { duration: 6000 });
      } else if (stats.contacts_created === 0 && stats.contacts_updated === 0) {
        toast.info(`${stats.total_submissions} soumissions trouvées, mais tous les contacts sont déjà dans le CRM.`, { duration: 5000 });
      } else {
        toast.success(
          `✅ Migration terminée ! ${stats.contacts_created} contacts créés, ${stats.contacts_updated} mis à jour, ${stats.contracts_linked} contrats liés.`,
          { duration: 6000 }
        );
      }

      if (response.data.errors && response.data.errors.length > 0) {
        console.warn('Erreurs lors de la migration:', response.data.errors);
        toast.warning(`${response.data.total_errors} erreurs rencontrées. Vérifiez la console.`, { duration: 4000 });
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error('❌ Erreur lors de la migration: ' + errorMsg, { duration: 6000 });
      console.error('Erreur migration CRM:', error);
    }
  };

  // Segment management functions
  const addSegment = () => {
    setSettings({
      ...settings,
      segments: [...(settings.segments || []), {
        id: Date.now().toString(),
        label: '',
        project_type: 'UHA',
        largeur_ft: 0,
        longueur_min_ft: 0,
        longueur_max_ft: 0,
        prix_par_pc: 0
      }]
    });
  };

  const removeSegment = (index) => {
    setSettings({
      ...settings,
      segments: settings.segments.filter((_, i) => i !== index)
    });
  };

  const updateSegment = (index, field, value) => {
    const newSegments = [...settings.segments];
    const numericFields = ['largeur_ft', 'longueur_min_ft', 'longueur_max_ft', 'prix_par_pc'];
    newSegments[index][field] = numericFields.includes(field) ? parseFloat(value) || 0 : value;
    setSettings({ ...settings, segments: newSegments });
  };

  const addInclusionCategory = (projectType) => {
    const newInclusions = [...settings.project_type_inclusions];
    const typeIndex = newInclusions.findIndex(pt => pt.project_type === projectType);
    
    if (typeIndex >= 0) {
      newInclusions[typeIndex].inclusions.push({
        category: '',
        items: []
      });
    } else {
      newInclusions.push({
        project_type: projectType,
        inclusions: [{ category: '', items: [] }]
      });
    }
    setSettings({ ...settings, project_type_inclusions: newInclusions });
  };

  const removeInclusionCategory = (projectType, categoryIndex) => {
    const newInclusions = [...settings.project_type_inclusions];
    const typeIndex = newInclusions.findIndex(pt => pt.project_type === projectType);
    
    if (typeIndex >= 0) {
      newInclusions[typeIndex].inclusions = newInclusions[typeIndex].inclusions.filter((_, i) => i !== categoryIndex);
      setSettings({ ...settings, project_type_inclusions: newInclusions });
    }
  };

  const updateInclusionCategory = (projectType, categoryIndex, field, value) => {
    const newInclusions = [...settings.project_type_inclusions];
    const typeIndex = newInclusions.findIndex(pt => pt.project_type === projectType);
    
    if (typeIndex >= 0) {
      if (field === 'category') {
        newInclusions[typeIndex].inclusions[categoryIndex].category = value;
      } else if (field === 'items') {
        newInclusions[typeIndex].inclusions[categoryIndex].items = value.split(',').map(item => item.trim()).filter(item => item);
      }
      setSettings({ ...settings, project_type_inclusions: newInclusions });
    }
  };

  const getInclusionsForType = (projectType) => {
    const typeInc = settings?.project_type_inclusions?.find(pt => pt.project_type === projectType);
    return typeInc?.inclusions || [];
  };

  const addOption = () => {
    setSettings({
      ...settings,
      options: [...settings.options, { name: '', label: '', price: 0, project_types: ['UHA', 'Maison de parc'] }]
    });
  };

  const removeOption = (index) => {
    setSettings({
      ...settings,
      options: settings.options.filter((_, i) => i !== index)
    });
  };

  const updateOption = (index, field, value) => {
    const newOptions = [...settings.options];
    newOptions[index][field] = field === 'price' ? parseFloat(value) || 0 : value;
    setSettings({ ...settings, options: newOptions });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#335C2E', borderTopColor: 'transparent' }}></div>
          <p style={{ color: '#666666' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" style={{ background: 'linear-gradient(180deg, #FAFAFA 0%, #F5F5F5 100%)' }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          {/* Navigation Header - Desktop */}
          <div className="hidden lg:flex flex-col gap-3 mb-4">
            {/* Ligne 1: Accueil + Boutons principaux */}
            <div className="flex justify-between items-center">
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center text-sm hover:underline"
                style={{ color: '#335C2E' }}
                data-testid="back-home-btn"
              >
                <Home className="w-4 h-4 mr-1" />
                Retour à l'accueil
              </button>
              
              {/* Boutons de navigation alignés */}
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => navigate('/admin/dashboard')}
                  className="inline-flex items-center text-xs px-2.5 py-1.5 rounded-lg hover:opacity-90"
                  style={{ background: '#E91E63', color: '#FFFFFF' }}
                  title="Dashboard KPI"
                >
                  <TrendingUp className="w-3.5 h-3.5 mr-1" />
                  KPI
                </button>
                <button
                  onClick={() => navigate('/admin/today')}
                  className="inline-flex items-center text-xs px-2.5 py-1.5 rounded-lg hover:opacity-90"
                  style={{ background: '#FF5722', color: '#FFFFFF' }}
                  title="À faire aujourd'hui"
                >
                  <ClipboardList className="w-3.5 h-3.5 mr-1" />
                  Aujourd'hui
                </button>
                <button
                  onClick={() => navigate('/admin/pipeline')}
                  className="inline-flex items-center text-xs px-2.5 py-1.5 rounded-lg hover:opacity-90"
                  style={{ background: '#673AB7', color: '#FFFFFF' }}
                  title="Pipeline commercial"
                >
                  <BarChart3 className="w-3.5 h-3.5 mr-1" />
                  Pipeline
                </button>
                <button
                  onClick={() => navigate('/admin/appointments')}
                  className="inline-flex items-center text-xs px-2.5 py-1.5 rounded-lg hover:opacity-90"
                  style={{ background: '#00897B', color: '#FFFFFF' }}
                  title="Gérer les rendez-vous"
                >
                  <Calendar className="w-3.5 h-3.5 mr-1" />
                  RDV
                </button>
                <button
                  onClick={() => navigate('/admin/crm')}
                  className="inline-flex items-center text-xs px-2.5 py-1.5 rounded-lg hover:opacity-90"
                  style={{ background: '#335C2E', color: '#FFFFFF' }}
                  title="Gérer les contacts CRM"
                >
                  <Users className="w-3.5 h-3.5 mr-1" />
                  CRM
                </button>
                <button
                  onClick={() => navigate('/admin/campaigns')}
                  className="inline-flex items-center text-xs px-2.5 py-1.5 rounded-lg hover:opacity-90"
                  style={{ background: '#2196F3', color: '#FFFFFF' }}
                  title="Campagnes d'emailing"
                >
                  <Mail className="w-3.5 h-3.5 mr-1" />
                  Mailing
                </button>
                <button
                  onClick={() => navigate('/admin/media-library')}
                  className="inline-flex items-center text-xs px-2.5 py-1.5 rounded-lg hover:opacity-90"
                  style={{ background: '#FF9800', color: '#FFFFFF' }}
                  title="Bibliothèque d'images"
                >
                  <Image className="w-3.5 h-3.5 mr-1" />
                  Médias
                </button>
                <button
                  onClick={() => navigate('/admin/blog')}
                  className="inline-flex items-center text-xs px-2.5 py-1.5 rounded-lg hover:opacity-90"
                  style={{ background: '#9C27B0', color: '#FFFFFF' }}
                  title="Gérer le blog"
                >
                  <FileText className="w-3.5 h-3.5 mr-1" />
                  Blog
                </button>
                <button
                  onClick={() => navigate('/admin/contracts')}
                  className="inline-flex items-center text-xs px-2.5 py-1.5 rounded-lg hover:opacity-90"
                  style={{ background: '#C7A027', color: '#FFFFFF' }}
                  title="Gérer les contrats"
                >
                  <FileText className="w-3.5 h-3.5 mr-1" />
                  Contrats
                </button>
                <button
                  onClick={() => navigate('/admin/activity-log')}
                  className="inline-flex items-center text-xs px-2.5 py-1.5 rounded-lg hover:opacity-90"
                  style={{ background: '#607D8B', color: '#FFFFFF' }}
                  title="Journal d'activité"
                >
                  <Activity className="w-3.5 h-3.5 mr-1" />
                  Journal
                </button>
                <button
                  onClick={handleLogout}
                  className="inline-flex items-center text-xs px-2.5 py-1.5 rounded-lg hover:bg-red-50"
                  style={{ color: '#DC2626' }}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-3.5 h-3.5 mr-1" />
                  Déconnexion
                </button>
              </div>
            </div>
          </div>

          {/* Navigation Header - Mobile/Tablet */}
          <div className="lg:hidden mb-4">
            <div className="flex justify-between items-center">
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center text-sm hover:underline"
                style={{ color: '#335C2E' }}
              >
                <Home className="w-4 h-4 mr-1" />
                Accueil
              </button>
              
              {/* Boutons principaux visibles sur tablette */}
              <div className="hidden sm:flex items-center gap-2">
                <button
                  onClick={() => navigate('/admin/dashboard')}
                  className="inline-flex items-center text-xs px-2 py-1.5 rounded-lg"
                  style={{ background: '#E91E63', color: '#FFFFFF' }}
                >
                  <TrendingUp className="w-3 h-3 mr-1" />
                  KPI
                </button>
                <button
                  onClick={() => navigate('/admin/pipeline')}
                  className="inline-flex items-center text-xs px-2 py-1.5 rounded-lg"
                  style={{ background: '#673AB7', color: '#FFFFFF' }}
                >
                  <BarChart3 className="w-3 h-3 mr-1" />
                  Pipeline
                </button>
                <button
                  onClick={() => navigate('/admin/crm')}
                  className="inline-flex items-center text-xs px-2 py-1.5 rounded-lg"
                  style={{ background: '#335C2E', color: '#FFFFFF' }}
                >
                  <Users className="w-3 h-3 mr-1" />
                  CRM
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="p-2 rounded-lg hover:bg-gray-100"
                  style={{ color: '#335C2E' }}
                >
                  {mobileMenuOpen ? <CloseIcon className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-lg hover:bg-red-50"
                  style={{ color: '#DC2626' }}
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Menu mobile déroulant */}
            {mobileMenuOpen && (
              <div className="mt-4 p-4 bg-white rounded-lg shadow-lg border">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <button
                    onClick={() => { navigate('/admin/dashboard'); setMobileMenuOpen(false); }}
                    className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-white text-sm"
                    style={{ background: '#E91E63' }}
                  >
                    <TrendingUp className="w-4 h-4" />
                    KPI
                  </button>
                  <button
                    onClick={() => { navigate('/admin/today'); setMobileMenuOpen(false); }}
                    className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-white text-sm"
                    style={{ background: '#FF5722' }}
                  >
                    <ClipboardList className="w-4 h-4" />
                    Aujourd'hui
                  </button>
                  <button
                    onClick={() => { navigate('/admin/pipeline'); setMobileMenuOpen(false); }}
                    className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-white text-sm"
                    style={{ background: '#673AB7' }}
                  >
                    <BarChart3 className="w-4 h-4" />
                    Pipeline
                  </button>
                  <button
                    onClick={() => { navigate('/admin/crm'); setMobileMenuOpen(false); }}
                    className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-white text-sm"
                    style={{ background: '#335C2E' }}
                  >
                    <Users className="w-4 h-4" />
                    CRM
                  </button>
                  <button
                    onClick={() => { navigate('/admin/campaigns'); setMobileMenuOpen(false); }}
                    className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-white text-sm"
                    style={{ background: '#2196F3' }}
                  >
                    <Mail className="w-4 h-4" />
                    Mailing
                  </button>
                  <button
                    onClick={() => { navigate('/admin/media-library'); setMobileMenuOpen(false); }}
                    className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-white text-sm"
                    style={{ background: '#FF9800' }}
                  >
                    <Image className="w-4 h-4" />
                    Médias
                  </button>
                  <button
                    onClick={() => { navigate('/admin/blog'); setMobileMenuOpen(false); }}
                    className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-white text-sm"
                    style={{ background: '#9C27B0' }}
                  >
                    <FileText className="w-4 h-4" />
                    Blog
                  </button>
                  <button
                    onClick={() => { navigate('/admin/contracts'); setMobileMenuOpen(false); }}
                    className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-white text-sm"
                    style={{ background: '#C7A027' }}
                  >
                    <FileText className="w-4 h-4" />
                    Contrats
                  </button>
                  <button
                    onClick={() => { navigate('/admin/appointments'); setMobileMenuOpen(false); }}
                    className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-white text-sm"
                    style={{ background: '#00897B' }}
                  >
                    <Calendar className="w-4 h-4" />
                    RDV
                  </button>
                  <button
                    onClick={() => { navigate('/admin/activity-log'); setMobileMenuOpen(false); }}
                    className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-white text-sm"
                    style={{ background: '#607D8B' }}
                  >
                    <Activity className="w-4 h-4" />
                    Journal
                  </button>
                </div>
              </div>
            )}
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold" style={{ color: '#335C2E' }}>
            Espace Admin
          </h1>
          {user && (
            <p className="text-sm mt-2" style={{ color: '#666666' }}>
              Connecté en tant que: <strong>{user.username}</strong>
            </p>
          )}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8">
          <div className="card text-center">
            <p className="text-sm mb-2" style={{ color: '#666666' }}>Total soumissions</p>
            <p className="text-3xl font-bold" style={{ color: '#335C2E' }}>{submissions.length}</p>
          </div>
          <div className="card text-center">
            <p className="text-sm mb-2" style={{ color: '#666666' }}>Segments configurés</p>
            <p className="text-3xl font-bold" style={{ color: '#C7A027' }}>{settings?.segments?.length || 0}</p>
          </div>
          <div className="card text-center">
            <p className="text-sm mb-2" style={{ color: '#666666' }}>Options disponibles</p>
            <p className="text-3xl font-bold" style={{ color: '#335C2E' }}>{settings?.options.length || 0}</p>
          </div>
        </div>

        {/* Boutons Actions rapides */}
        <div className="mb-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/admin/contracts')}
            className="flex items-center justify-center gap-2 px-6 py-3 text-white rounded-lg text-lg font-semibold hover:opacity-90"
            style={{ background: '#C7A027' }}
          >
            <FileText size={24} />
            Gérer les contrats de vente
          </button>
          <button
            onClick={() => navigate('/admin/document-styles')}
            className="flex items-center justify-center gap-2 px-6 py-3 text-white rounded-lg text-lg font-semibold hover:opacity-90"
            style={{ background: '#335C2E' }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2v6m0 12v6m-6-6h12M6 12H2m20 0h-4"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
            Configuration des styles
          </button>
          <button
            onClick={() => navigate('/admin/app-settings')}
            className="flex items-center justify-center gap-2 px-6 py-3 text-white rounded-lg text-lg font-semibold hover:opacity-90"
            style={{ background: '#9C27B0' }}
            title="Gérer les paramètres de l'application"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 1v6m0 6v6m6-12l-6 6m0 0l-6 6m18-6l-6-6m0 0l-6 6"/>
              <path d="M19.4 15.7a7 7 0 0 1-2.7 2.7m-9.4 0a7 7 0 0 1-2.7-2.7m0-7.4a7 7 0 0 1 2.7-2.7m7.4 0a7 7 0 0 1 2.7 2.7"/>
            </svg>
            Paramètres Application
          </button>
          <button
            onClick={() => navigate('/admin/legal-settings')}
            className="flex items-center justify-center gap-2 px-6 py-3 text-white rounded-lg text-lg font-semibold hover:opacity-90"
            style={{ background: '#607D8B' }}
            title="Gérer les pages légales et demandes RGPD"
          >
            <Shield size={24} />
            Pages Légales & RGPD
          </button>
          <button
            onClick={() => navigate('/admin/appointments')}
            className="flex items-center justify-center gap-2 px-6 py-3 text-white rounded-lg text-lg font-semibold hover:opacity-90"
            style={{ background: '#00897B' }}
            title="Gérer les rendez-vous et le calendrier"
          >
            <Calendar size={24} />
            📅 Gestion des Rendez-vous
          </button>
          <button
            onClick={async () => {
              try {
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/guide-utilisation/pdf`);
                if (!response.ok) throw new Error('Erreur lors du téléchargement');
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'guide_utilisation_refacing_vr.pdf';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                toast.success('Guide téléchargé avec succès!');
              } catch (error) {
                toast.error('Erreur lors du téléchargement du guide');
              }
            }}
            className="flex items-center justify-center gap-2 px-6 py-3 text-white rounded-lg text-lg font-semibold hover:opacity-90"
            style={{ background: '#1976D2' }}
            title="Télécharger le guide d'utilisation de l'application"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2v12m0 0l-4-4m4 4l4-4"/>
              <path d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2"/>
            </svg>
            📘 Guide d'utilisation
          </button>
        </div>

        {/* Settings Accordion */}
        <div className="card mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold" style={{ color: '#333333' }}>Paramètres</h2>
            <button
              onClick={updateSettings}
              disabled={saving}
              className="btn-primary"
              data-testid="save-settings-btn"
            >
              <Save className="inline-block mr-2 w-5 h-5" />
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {/* Segments */}
            <AccordionItem value="segments">
              <AccordionTrigger className="text-lg font-semibold" style={{ color: '#335C2E' }}>
                Segments de prix (Largeur × Longueur)
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 pt-4">
                  {settings?.segments?.map((segment, index) => (
                    <div key={segment.id || index} className="space-y-3 p-4 rounded-lg border-l-4" style={{ background: '#F5F5F5', borderColor: '#335C2E' }}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1 grid grid-cols-2 gap-4">
                          {/* Label */}
                          <div className="col-span-2">
                            <label className="block text-xs mb-1 font-medium" style={{ color: '#666666' }}>
                              Intitulé du segment
                            </label>
                            <input
                              type="text"
                              value={segment.label}
                              onChange={(e) => updateSegment(index, 'label', e.target.value)}
                              placeholder="Ex: UHA 10' × 18-24'"
                              className="input-field w-full"
                            />
                          </div>

                          {/* Project Type */}
                          <div>
                            <label className="block text-xs mb-1 font-medium" style={{ color: '#666666' }}>
                              Type de projet
                            </label>
                            <select
                              value={segment.project_type}
                              onChange={(e) => updateSegment(index, 'project_type', e.target.value)}
                              className="input-field w-full"
                            >
                              <option value="UHA">UHA</option>
                              <option value="Maison de parc">Maison de parc</option>
                            </select>
                          </div>

                          {/* Largeur */}
                          <div>
                            <label className="block text-xs mb-1 font-medium" style={{ color: '#666666' }}>
                              Largeur (pieds)
                            </label>
                            <input
                              type="number"
                              value={segment.largeur_ft}
                              onChange={(e) => updateSegment(index, 'largeur_ft', e.target.value)}
                              placeholder="10"
                              className="input-field w-full"
                              step="0.1"
                            />
                          </div>

                          {/* Longueur Min */}
                          <div>
                            <label className="block text-xs mb-1 font-medium" style={{ color: '#666666' }}>
                              Longueur min (pieds)
                            </label>
                            <input
                              type="number"
                              value={segment.longueur_min_ft}
                              onChange={(e) => updateSegment(index, 'longueur_min_ft', e.target.value)}
                              placeholder="18"
                              className="input-field w-full"
                              step="0.1"
                            />
                          </div>

                          {/* Longueur Max */}
                          <div>
                            <label className="block text-xs mb-1 font-medium" style={{ color: '#666666' }}>
                              Longueur max (pieds)
                            </label>
                            <input
                              type="number"
                              value={segment.longueur_max_ft}
                              onChange={(e) => updateSegment(index, 'longueur_max_ft', e.target.value)}
                              placeholder="24"
                              className="input-field w-full"
                              step="0.1"
                            />
                          </div>

                          {/* Prix par PC */}
                          <div>
                            <label className="block text-xs mb-1 font-medium" style={{ color: '#666666' }}>
                              Prix par pi² ($)
                            </label>
                            <input
                              type="number"
                              value={segment.prix_par_pc}
                              onChange={(e) => updateSegment(index, 'prix_par_pc', e.target.value)}
                              placeholder="375"
                              className="input-field w-full"
                              step="0.01"
                            />
                          </div>
                        </div>

                        {/* Delete Button */}
                        <button
                          onClick={() => removeSegment(index)}
                          className="p-2 rounded hover:bg-red-100 ml-4"
                        >
                          <Trash2 className="w-5 h-5" style={{ color: '#DC2626' }} />
                        </button>
                      </div>

                      {/* Preview */}
                      <div className="text-xs p-2 rounded" style={{ background: '#E8F5E9', color: '#2E7D32' }}>
                        <strong>Aperçu :</strong> {segment.project_type} - Largeur {segment.largeur_ft}' × Longueur {segment.longueur_min_ft}' à {segment.longueur_max_ft}' = {segment.prix_par_pc}$/pi²
                      </div>
                    </div>
                  ))}
                  <button onClick={addSegment} className="btn-secondary w-full">
                    <Plus className="inline-block mr-2 w-5 h-5" />
                    Ajouter un segment
                  </button>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Options */}
            <AccordionItem value="options">
              <AccordionTrigger className="text-lg font-semibold" style={{ color: '#335C2E' }}>
                Options et prix
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 pt-4">
                  {settings?.options.map((option, index) => (
                    <div key={index} className="space-y-2 p-4 rounded-lg" style={{ background: '#F5F5F5' }}>
                      <div className="flex gap-4 items-center">
                        <div className="flex-1">
                          <label className="block text-xs mb-1" style={{ color: '#666666' }}>Nom technique (clé)</label>
                          <input
                            type="text"
                            value={option.name}
                            onChange={(e) => updateOption(index, 'name', e.target.value)}
                            placeholder="Ex: Thermopompe"
                            className="input-field w-full"
                            data-testid={`option-name-${index}`}
                          />
                        </div>
                        <div className="w-32">
                          <label className="block text-xs mb-1" style={{ color: '#666666' }}>Prix</label>
                          <input
                            type="number"
                            value={option.price}
                            onChange={(e) => updateOption(index, 'price', e.target.value)}
                            placeholder="2800"
                            className="input-field w-full"
                            data-testid={`option-price-${index}`}
                          />
                        </div>
                        <button
                          onClick={() => removeOption(index)}
                          className="p-2 rounded hover:bg-red-100 self-end"
                          data-testid={`remove-option-${index}`}
                        >
                          <Trash2 className="w-5 h-5" style={{ color: '#DC2626' }} />
                        </button>
                      </div>
                      <div>
                        <label className="block text-xs mb-1" style={{ color: '#666666' }}>Intitulé/Label (affiché au client)</label>
                        <input
                          type="text"
                          value={option.label || ''}
                          onChange={(e) => updateOption(index, 'label', e.target.value)}
                          placeholder="Ex: Climatiseur mural / Thermopompe"
                          className="input-field w-full"
                          data-testid={`option-label-${index}`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs mb-2" style={{ color: '#666666' }}>Types de projets compatibles</label>
                        <div className="flex gap-4">
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={option.project_types?.includes('UHA') ?? true}
                              onChange={(e) => {
                                const currentTypes = option.project_types || ['UHA', 'Maison de parc'];
                                const newTypes = e.target.checked
                                  ? [...new Set([...currentTypes, 'UHA'])]
                                  : currentTypes.filter(t => t !== 'UHA');
                                updateOption(index, 'project_types', newTypes);
                              }}
                              className="w-4 h-4"
                              data-testid={`option-type-uha-${index}`}
                            />
                            <span className="text-sm" style={{ color: '#333333' }}>UHA</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={option.project_types?.includes('Maison de parc') ?? true}
                              onChange={(e) => {
                                const currentTypes = option.project_types || ['UHA', 'Maison de parc'];
                                const newTypes = e.target.checked
                                  ? [...new Set([...currentTypes, 'Maison de parc'])]
                                  : currentTypes.filter(t => t !== 'Maison de parc');
                                updateOption(index, 'project_types', newTypes);
                              }}
                              className="w-4 h-4"
                              data-testid={`option-type-maison-${index}`}
                            />
                            <span className="text-sm" style={{ color: '#333333' }}>Maison de parc</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button onClick={addOption} className="btn-secondary w-full" data-testid="add-option-btn">
                    <Plus className="inline-block mr-2 w-5 h-5" />
                    Ajouter une option
                  </button>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Inclusions par type de projet */}
            <AccordionItem value="inclusions">
              <AccordionTrigger className="text-lg font-semibold" style={{ color: '#335C2E' }}>
                Inclusions standards par type de projet
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-6 pt-4">
                  {/* Maison de parc */}
                  <div className="p-4 rounded-lg border-2" style={{ borderColor: '#335C2E', background: '#FAFAFA' }}>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-bold" style={{ color: '#335C2E' }}>Maison de parc</h3>
                      <button
                        onClick={() => addInclusionCategory('Maison de parc')}
                        className="btn-secondary text-sm px-4 py-2"
                        data-testid="add-inclusion-maisondeparc"
                      >
                        + Ajouter catégorie
                      </button>
                    </div>
                    
                    <div className="space-y-3">
                      {getInclusionsForType('Maison de parc').map((inclusion, index) => (
                        <div key={index} className="p-3 rounded-lg" style={{ background: '#F5F5F5' }}>
                          <div className="flex gap-2 items-start">
                            <div className="flex-1">
                              <input
                                type="text"
                                value={inclusion.category}
                                onChange={(e) => updateInclusionCategory('Maison de parc', index, 'category', e.target.value)}
                                placeholder="Ex: Cuisine complète"
                                className="input-field w-full mb-2"
                                data-testid={`inclusion-maisondeparc-category-${index}`}
                              />
                              <textarea
                                value={inclusion.items.join(', ')}
                                onChange={(e) => updateInclusionCategory('Maison de parc', index, 'items', e.target.value)}
                                placeholder="Items séparés par des virgules: Évier, Robinetterie, Hotte"
                                className="input-field w-full"
                                rows={2}
                                data-testid={`inclusion-maisondeparc-items-${index}`}
                              />
                            </div>
                            <button
                              onClick={() => removeInclusionCategory('Maison de parc', index)}
                              className="p-2 rounded hover:bg-red-100"
                              data-testid={`remove-inclusion-maisondeparc-${index}`}
                            >
                              <Trash2 className="w-4 h-4" style={{ color: '#DC2626' }} />
                            </button>
                          </div>
                        </div>
                      ))}
                      
                      {getInclusionsForType('Maison de parc').length === 0 && (
                        <p className="text-center py-4" style={{ color: '#999999' }}>
                          Aucune inclusion configurée pour Maison de parc
                        </p>
                      )}
                    </div>
                  </div>

                  {/* UHA */}
                  <div className="p-4 rounded-lg border-2" style={{ borderColor: '#C7A027', background: '#FAFAFA' }}>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-bold" style={{ color: '#C7A027' }}>UHA</h3>
                      <button
                        onClick={() => addInclusionCategory('UHA')}
                        className="btn-secondary text-sm px-4 py-2"
                        data-testid="add-inclusion-uha"
                      >
                        + Ajouter catégorie
                      </button>
                    </div>
                    
                    <div className="space-y-3">
                      {getInclusionsForType('UHA').map((inclusion, index) => (
                        <div key={index} className="p-3 rounded-lg" style={{ background: '#F5F5F5' }}>
                          <div className="flex gap-2 items-start">
                            <div className="flex-1">
                              <input
                                type="text"
                                value={inclusion.category}
                                onChange={(e) => updateInclusionCategory('UHA', index, 'category', e.target.value)}
                                placeholder="Ex: Cuisine"
                                className="input-field w-full mb-2"
                                data-testid={`inclusion-uha-category-${index}`}
                              />
                              <textarea
                                value={inclusion.items.join(', ')}
                                onChange={(e) => updateInclusionCategory('UHA', index, 'items', e.target.value)}
                                placeholder="Items séparés par des virgules: Évier, Robinetterie, Armoires"
                                className="input-field w-full"
                                rows={2}
                                data-testid={`inclusion-uha-items-${index}`}
                              />
                            </div>
                            <button
                              onClick={() => removeInclusionCategory('UHA', index)}
                              className="p-2 rounded hover:bg-red-100"
                              data-testid={`remove-inclusion-uha-${index}`}
                            >
                              <Trash2 className="w-4 h-4" style={{ color: '#DC2626' }} />
                            </button>
                          </div>
                        </div>
                      ))}
                      
                      {getInclusionsForType('UHA').length === 0 && (
                        <p className="text-center py-4" style={{ color: '#999999' }}>
                          Aucune inclusion configurée pour UHA
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Taxes */}
            <AccordionItem value="taxes">
              <AccordionTrigger className="text-lg font-semibold" style={{ color: '#335C2E' }}>
                Taxes
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 pt-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#666666' }}>TPS (%)</label>
                    <input
                      type="number"
                      step="0.001"
                      value={settings?.tps_rate * 100}
                      onChange={(e) => setSettings({ ...settings, tps_rate: parseFloat(e.target.value) / 100 })}
                      className="input-field"
                      data-testid="tps-rate-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#666666' }}>TVQ (%)</label>
                    <input
                      type="number"
                      step="0.001"
                      value={settings?.tvq_rate * 100}
                      onChange={(e) => setSettings({ ...settings, tvq_rate: parseFloat(e.target.value) / 100 })}
                      className="input-field"
                      data-testid="tvq-rate-input"
                    />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Payment Percentages */}
            <AccordionItem value="payment">
              <AccordionTrigger className="text-lg font-semibold" style={{ color: '#335C2E' }}>
                Pourcentages de paiement
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid md:grid-cols-2 gap-4 pt-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#666666' }}>Dépôt (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={settings?.payment_percentages.deposit * 100}
                      onChange={(e) => setSettings({
                        ...settings,
                        payment_percentages: { ...settings.payment_percentages, deposit: parseFloat(e.target.value) / 100 }
                      })}
                      className="input-field"
                      data-testid="deposit-percent-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#666666' }}>Lancement (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={settings?.payment_percentages.launch * 100}
                      onChange={(e) => setSettings({
                        ...settings,
                        payment_percentages: { ...settings.payment_percentages, launch: parseFloat(e.target.value) / 100 }
                      })}
                      className="input-field"
                      data-testid="launch-percent-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#666666' }}>Milieu (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={settings?.payment_percentages.middle * 100}
                      onChange={(e) => setSettings({
                        ...settings,
                        payment_percentages: { ...settings.payment_percentages, middle: parseFloat(e.target.value) / 100 }
                      })}
                      className="input-field"
                      data-testid="middle-percent-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#666666' }}>Livraison (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={settings?.payment_percentages.delivery * 100}
                      onChange={(e) => setSettings({
                        ...settings,
                        payment_percentages: { ...settings.payment_percentages, delivery: parseFloat(e.target.value) / 100 }
                      })}
                      className="input-field"
                      data-testid="delivery-percent-input"
                    />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Calendar Offsets */}
            <AccordionItem value="calendar">
              <AccordionTrigger className="text-lg font-semibold" style={{ color: '#335C2E' }}>
                Offsets du calendrier (jours)
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid md:grid-cols-2 gap-4 pt-4">
                  {Object.entries(settings?.calendar_offsets || {}).map(([key, value]) => (
                    <div key={key}>
                      <label className="block text-sm font-medium mb-2 capitalize" style={{ color: '#666666' }}>{key}</label>
                      <input
                        type="number"
                        value={value}
                        onChange={(e) => setSettings({
                          ...settings,
                          calendar_offsets: { ...settings.calendar_offsets, [key]: parseInt(e.target.value) || 0 }
                        })}
                        className="input-field"
                        data-testid={`calendar-${key}-input`}
                      />
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Appointment Settings */}
            <AccordionItem value="appointments">
              <AccordionTrigger className="text-lg font-semibold" style={{ color: '#335C2E' }}>
                Paramètres de rendez-vous
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-6 pt-4">
                  {/* Duration */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#666666' }}>
                      Durée d'un rendez-vous (minutes)
                    </label>
                    <input
                      type="number"
                      value={settings?.appointment_settings?.duration_minutes || 90}
                      onChange={(e) => setSettings({
                        ...settings,
                        appointment_settings: {
                          ...settings.appointment_settings,
                          duration_minutes: parseInt(e.target.value) || 90
                        }
                      })}
                      className="input-field w-32"
                      data-testid="appointment-duration-input"
                    />
                  </div>

                  {/* Days Configuration */}
                  <div>
                    <h4 className="text-sm font-semibold mb-4" style={{ color: '#666666' }}>Horaires par jour</h4>
                    <div className="space-y-4">
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                        const dayLabels = {
                          monday: 'Lundi',
                          tuesday: 'Mardi',
                          wednesday: 'Mercredi',
                          thursday: 'Jeudi',
                          friday: 'Vendredi',
                          saturday: 'Samedi',
                          sunday: 'Dimanche'
                        };
                        const daySettings = settings?.appointment_settings?.[day] || { enabled: false, start: '09:00', end: '17:00' };
                        
                        return (
                          <div key={day} className="p-4 rounded-lg" style={{ background: '#F5F5F5' }}>
                            <div className="flex items-center justify-between mb-3">
                              <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={daySettings.enabled}
                                  onChange={(e) => setSettings({
                                    ...settings,
                                    appointment_settings: {
                                      ...settings.appointment_settings,
                                      [day]: { ...daySettings, enabled: e.target.checked }
                                    }
                                  })}
                                  className="w-4 h-4"
                                />
                                <span className="font-medium" style={{ color: '#333333' }}>{dayLabels[day]}</span>
                              </label>
                            </div>
                            
                            {daySettings.enabled && (
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-xs mb-1" style={{ color: '#666666' }}>Heure de début</label>
                                  <input
                                    type="time"
                                    value={daySettings.start}
                                    onChange={(e) => setSettings({
                                      ...settings,
                                      appointment_settings: {
                                        ...settings.appointment_settings,
                                        [day]: { ...daySettings, start: e.target.value }
                                      }
                                    })}
                                    className="input-field"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs mb-1" style={{ color: '#666666' }}>Heure de fin</label>
                                  <input
                                    type="time"
                                    value={daySettings.end}
                                    onChange={(e) => setSettings({
                                      ...settings,
                                      appointment_settings: {
                                        ...settings.appointment_settings,
                                        [day]: { ...daySettings, end: e.target.value }
                                      }
                                    })}
                                    className="input-field"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Password Change Section */}
            <AccordionItem value="password">
              <AccordionTrigger>
                <span className="text-lg font-semibold">🔒 Changer le mot de passe</span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="p-6">
                  <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#333333' }}>
                        Ancien mot de passe
                      </label>
                      <input
                        type="password"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        className="w-full p-3 border rounded-lg"
                        style={{ borderColor: '#E5E5E5' }}
                        placeholder="Entrez votre ancien mot de passe"
                        data-testid="old-password-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#333333' }}>
                        Nouveau mot de passe
                      </label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full p-3 border rounded-lg"
                        style={{ borderColor: '#E5E5E5' }}
                        placeholder="Minimum 8 caractères"
                        data-testid="new-password-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#333333' }}>
                        Confirmer le nouveau mot de passe
                      </label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full p-3 border rounded-lg"
                        style={{ borderColor: '#E5E5E5' }}
                        placeholder="Retapez le nouveau mot de passe"
                        data-testid="confirm-password-input"
                      />
                    </div>
                    
                    <button
                      type="submit"
                      disabled={changingPassword}
                      className="btn-primary"
                      data-testid="change-password-btn"
                    >
                      {changingPassword ? 'Changement...' : 'Changer le mot de passe'}
                    </button>
                  </form>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {/* Submissions List */}
        <div className="card">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold" style={{ color: '#333333' }}>Soumissions ({filteredSubmissions.length})</h2>
            <button onClick={exportCSV} className="btn-secondary" data-testid="export-csv-btn">
              <Download className="inline-block mr-2 w-5 h-5" />
              Exporter CSV
            </button>
          </div>

          {/* Search and Filter Section */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border" style={{ borderColor: '#E5E5E5' }}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#333333' }}>
                  🔍 Recherche (nom, email, modèle)
                </label>
                <input
                  type="text"
                  value={submissionSearch}
                  onChange={(e) => setSubmissionSearch(e.target.value)}
                  placeholder="Rechercher..."
                  className="w-full p-2 border rounded-lg"
                  style={{ borderColor: '#E5E5E5' }}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#333333' }}>
                  📅 Filtrer par date
                </label>
                <input
                  type="date"
                  value={submissionDateFilter}
                  onChange={(e) => setSubmissionDateFilter(e.target.value)}
                  className="w-full p-2 border rounded-lg"
                  style={{ borderColor: '#E5E5E5' }}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#333333' }}>
                  🏠 Filtrer par modèle
                </label>
                <input
                  type="text"
                  value={submissionModelFilter}
                  onChange={(e) => setSubmissionModelFilter(e.target.value)}
                  placeholder="Ex: UHA, Gazébo..."
                  className="w-full p-2 border rounded-lg"
                  style={{ borderColor: '#E5E5E5' }}
                />
              </div>
            </div>
            
            {(submissionSearch || submissionDateFilter || submissionModelFilter) && (
              <button
                onClick={() => {
                  setSubmissionSearch('');
                  setSubmissionDateFilter('');
                  setSubmissionModelFilter('');
                }}
                className="mt-3 text-sm px-3 py-1 rounded hover:bg-gray-200"
                style={{ color: '#335C2E' }}
              >
                ✕ Réinitialiser les filtres
              </button>
            )}
          </div>

          {/* Table with scrollbar */}
          <div className="overflow-x-auto" style={{ maxHeight: '600px', overflowY: 'auto' }}>
            <table className="w-full">
              <thead className="sticky top-0" style={{ background: '#F5F5F5', zIndex: 10 }}>
                <tr>
                  <th className="text-left p-3 font-semibold" style={{ color: '#333333' }}>Date</th>
                  <th className="text-left p-3 font-semibold" style={{ color: '#333333' }}>Client</th>
                  <th className="text-left p-3 font-semibold" style={{ color: '#333333' }}>Email</th>
                  <th className="text-left p-3 font-semibold" style={{ color: '#333333' }}>Modèle</th>
                  <th className="text-left p-3 font-semibold" style={{ color: '#333333' }}>Options</th>
                  <th className="text-right p-3 font-semibold" style={{ color: '#333333' }}>Total TTC</th>
                  <th className="text-center p-3 font-semibold" style={{ color: '#333333' }}>Précontrat</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubmissions.map((sub) => (
                  <tr key={sub.id} className="border-b hover:bg-gray-50" style={{ borderColor: '#E5E5E5' }}>
                    <td className="p-3 text-sm" style={{ color: '#666666' }}>
                      {new Date(sub.created_at).toLocaleDateString('fr-CA')}
                    </td>
                    <td className="p-3 text-sm" style={{ color: '#333333' }}>{sub.customer.name}</td>
                    <td className="p-3 text-sm" style={{ color: '#666666' }}>{sub.customer.email}</td>
                    <td className="p-3 text-sm" style={{ color: '#666666' }}>{sub.project.model}</td>
                    <td className="p-3 text-sm" style={{ color: '#666666' }}>
                      {sub.project.options && sub.project.options.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {sub.project.options.slice(0, 2).map((opt, idx) => (
                            <span key={idx} className="px-2 py-0.5 rounded text-xs" style={{ background: '#E8F5E9', color: '#2E7D32' }}>
                              {opt}
                            </span>
                          ))}
                          {sub.project.options.length > 2 && (
                            <span className="px-2 py-0.5 rounded text-xs" style={{ background: '#FFF3E0', color: '#E65100' }}>
                              +{sub.project.options.length - 2}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs italic text-gray-400">Aucune</span>
                      )}
                    </td>
                    <td className="p-3 text-sm text-right font-semibold" style={{ color: '#C7A027' }}>
                      {sub.calculation.total_ttc.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-center">
                        <button
                          onClick={() => downloadPrecontractPDF(sub.id, sub.customer.name)}
                          className="p-2 rounded hover:bg-green-100 text-lg"
                          title="Télécharger PDF"
                        >
                          📄
                        </button>
                        <button
                          onClick={() => downloadPrecontractWord(sub.id, sub.customer.name)}
                          className="p-2 rounded hover:bg-yellow-100 text-lg"
                          title="Télécharger Word"
                        >
                          📝
                        </button>
                        <button
                          onClick={() => deleteSubmission(sub.id, sub.customer.name)}
                          className="p-2 rounded hover:bg-red-100 text-lg"
                          title="Supprimer le précontrat"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {submissions.length === 0 && (
              <p className="text-center py-8" style={{ color: '#999999' }}>Aucune soumission pour le moment</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
